import React, { useEffect, useState } from "react";

import Drawer from "../components/Drawer";

import { SudsMap } from "../maps";

import { filterVisibilities } from "../assets/filters";

import { styled, useTheme } from "@mui/material/styles";

import { interpolateBlues } from "d3-scale-chromatic";

import { useMap } from "react-map-gl";
import {
  boroughColour,
  giglOpenSpaceColour,
  thamesCombinedColour,
  thamesSeparateColour,
  watercoursesColour,
} from "../assets/colours";
import { initialSitesProprties } from "../assets/sites";
import { Box, useMediaQuery } from "@mui/material";

const MapContainer = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile",
})(({ theme, open, isMobile }) => ({
  position: "absolute",
  top: 0,
  flexGrow: 1,
  height: isMobile ? "40%" : "100%",

  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: "100%",
  marginRight: isMobile ? "0px" : `450px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: 0,
    }),
    marginRight: 0,
    marginTop: 0,
    marginBottom: 0,
    height: "100%",
  }),
}));

const Main = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [sitesProperties, setSitesProperties] = useState(
    JSON.parse(JSON.stringify(initialSitesProprties))
  );
  //JSON.parse and stringify clones the initial state so that it isn't mutated for when used again later
  const [filtersProperties, setFiltersProperties] = useState(
    filterVisibilities()
  );
  const [catchmentPartnershipProperties, setCatchmentPartnershipProperties] =
    useState({});
  const [noFilters, setNoFilters] = useState(true);
  const [giglOpenSpaceProperties, setGiglOpenSpaceProperties] = useState({
    "GiGL Open Space": {
      visible: false,
      color: giglOpenSpaceColour,
    },
  });
  const [thamesCombinedProperties, setThamesCombinedProperties] = useState({
    "Thames Water Combined Sewer": {
      visible: false,
      color: thamesCombinedColour,
    },
  });
  const [thamesSeperateProperties, setThamesSeperateProperties] = useState({
    "Thames Water Separate Sewer": {
      visible: false,
      color: thamesSeparateColour,
    },
  });
  const [watercoursesProperties, setWatercoursesProperties] = useState({
    Water: {
      visible: false,
      color: watercoursesColour,
    },
  });
  const [mapProperties, setMapProperties] = useState({
    "Dark Mode": { visible: false, color: "black" },
  });
  const [boroughProperties, setBoroughProperties] = useState({
    "Borough Boundaries": { visible: true, color: boroughColour },
  });
  const [catchmentOpacityOffset, setCatchmentOpacityOffset] = useState(0);
  const [giglOpacityOffset, setGiglOpacityOffset] = useState(0);
  const [thamesCombinedOpacityOffset, setThamesCombinedOpacityOffset] =
    useState(0);
  const [thamesSeparateOpacityOffset, setThamesSeparateOpacityOffset] =
    useState(0);
  const [watercoursesOpacityOffset, setWatercoursesOpacityOffset] = useState(0);
  const { map } = useMap();

  const [drawerOpen, setDrawerOpen] = useState(true);

  useEffect(() => {
    const initialCatchmentPartnershipProperties = getInitialSourceProperties(
      map,
      "catchment-partnership",
      interpolateBlues,
      false,
      "caba_partn"
    );
    if (
      Object.keys(catchmentPartnershipProperties).length === 0 &&
      initialCatchmentPartnershipProperties
    ) {
      setCatchmentPartnershipProperties(initialCatchmentPartnershipProperties);
    }
  }, [map, catchmentPartnershipProperties, setCatchmentPartnershipProperties]);

  return (
    <Box
      sx={{
        height: "100%",
        maxHeight: "100%",
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <Drawer
        open={drawerOpen}
        setOpen={setDrawerOpen}
        drawerContentProps={{
          setSitesProperties,
          sitesProperties,
          filtersProperties,
          setFiltersProperties,
          noFilters,
          setNoFilters,
          catchmentPartnershipProperties,
          setCatchmentPartnershipProperties,
          giglOpenSpaceProperties,
          setGiglOpenSpaceProperties,
          thamesCombinedProperties,
          setThamesCombinedProperties,
          thamesSeperateProperties,
          setThamesSeperateProperties,
          watercoursesProperties,
          setWatercoursesProperties,
          boroughProperties,
          setBoroughProperties,
          mapProperties,
          setMapProperties,
          catchmentOpacityOffset,
          setCatchmentOpacityOffset,
          giglOpacityOffset,
          setGiglOpacityOffset,
          thamesCombinedOpacityOffset,
          setThamesCombinedOpacityOffset,
          thamesSeparateOpacityOffset,
          setThamesSeparateOpacityOffset,
          watercoursesOpacityOffset,
          setWatercoursesOpacityOffset,
        }}
      ></Drawer>

      <MapContainer open={!drawerOpen} isMobile={isMobile} id={"map-container"}>
        <SudsMap
          checkedSites={sitesProperties}
          filtersProperties={filtersProperties}
          noFilters={noFilters}
          catchmentPartnershipProperties={catchmentPartnershipProperties}
          drawerOpen={drawerOpen}
          giglOpenSpaceProperties={giglOpenSpaceProperties}
          thamesCombinedProperties={thamesCombinedProperties}
          thamesSeperateProperties={thamesSeperateProperties}
          watercoursesProperties={watercoursesProperties}
          boroughProperties={boroughProperties}
          mapProperties={mapProperties}
          catchmentOpacityOffset={catchmentOpacityOffset}
          giglOpacityOffset={giglOpacityOffset}
          thamesCombinedOpacityOffset={thamesCombinedOpacityOffset}
          thamesSeparateOpacityOffset={thamesSeparateOpacityOffset}
          watercoursesOpacityOffset={watercoursesOpacityOffset}
        />
      </MapContainer>
    </Box>
  );
};

export default Main;

function filterDuplicates(features, pathOfName) {
  return Array.from(
    new Set(features.map((item) => item.properties[pathOfName]))
  ).map((name) => {
    return features.find((a) => a.properties[pathOfName] === name);
  });
}

function getInitialSourceProperties(
  map,
  sourceId,
  colourInterpolateFunction,
  initialVisibility,
  pathOfName
) {
  const features = map?.querySourceFeatures(sourceId);

  if (!features) {
    return {};
  }

  const uniqueFeatures = filterDuplicates(features, pathOfName);

  const normalisingIndex = uniqueFeatures.length - 1;

  const newChecked = uniqueFeatures?.reduce(
    (a, v, index) => ({
      ...a,
      [v.properties[pathOfName]]: {
        visible: initialVisibility,
        pathOfName,
        color: colourInterpolateFunction(index / normalisingIndex),
      },
    }),
    {}
  );
  return newChecked;
}
